<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    required: true,
  },
  emptyMessage: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue', 'search']);
const isOpen = ref(false);
const query = ref('');

const filteredOptions = computed(() => {
  const search = query.value.trim().toLocaleLowerCase();
  if (!search) return props.options;

  return props.options.filter(option =>
    option.label.toLocaleLowerCase().includes(search)
  );
});

const selectedLabels = computed(() =>
  props.options
    .filter(option => props.modelValue.includes(option.value))
    .map(option => option.label)
);

const toggleOption = value => {
  const nextValue = props.modelValue.includes(value)
    ? props.modelValue.filter(item => item !== value)
    : [...props.modelValue, value];
  emit('update:modelValue', nextValue);
};

const close = () => {
  isOpen.value = false;
  query.value = '';
};
</script>

<template>
  <div v-on-click-outside="close" class="relative min-w-0">
    <button
      type="button"
      class="flex min-h-10 w-full items-center gap-2 rounded-xl border border-n-strong bg-n-solid-3 px-3 py-2 text-left text-sm transition hover:border-n-slate-8 hover:bg-n-solid-2 focus:border-n-brand focus:outline-none"
      :class="{ 'border-n-brand bg-n-solid-2': isOpen }"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <span class="i-lucide-tags size-4 shrink-0 text-n-slate-9" />
      <span
        class="min-w-0 flex-1 truncate"
        :class="selectedLabels.length ? 'text-n-slate-12' : 'text-n-slate-10'"
      >
        {{ selectedLabels.length ? selectedLabels.join(', ') : placeholder }}
      </span>
      <span
        v-if="selectedLabels.length"
        class="rounded-full bg-n-brand/15 px-2 py-0.5 text-xs font-medium text-n-brand"
      >
        {{ selectedLabels.length }}
      </span>
      <span
        class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-10 transition"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute left-0 right-0 top-full z-50 mt-1.5 rounded-xl border border-n-strong bg-n-solid-2 p-2 shadow-xl"
    >
      <div class="relative">
        <span
          class="i-lucide-search absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-9"
        />
        <input
          v-model="query"
          type="search"
          :placeholder="searchPlaceholder"
          class="no-margin h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 py-0 pl-9 pr-3 text-sm leading-9 text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-n-brand"
          @input="emit('search', query)"
          @keydown.esc="close"
        />
      </div>

      <div class="mt-2 max-h-48 overflow-y-auto">
        <button
          v-for="option in filteredOptions"
          :key="option.value"
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-n-slate-11 transition hover:bg-n-alpha-2"
          @click="toggleOption(option.value)"
        >
          <span
            class="flex size-4 shrink-0 items-center justify-center rounded border"
            :class="
              modelValue.includes(option.value)
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak bg-n-alpha-1'
            "
          >
            <span
              v-if="modelValue.includes(option.value)"
              class="i-lucide-check size-3"
            />
          </span>
          <span class="truncate">{{ option.label }}</span>
        </button>

        <p
          v-if="filteredOptions.length === 0"
          class="px-3 py-4 text-center text-sm text-n-slate-9"
        >
          {{ emptyMessage }}
        </p>
      </div>
    </div>
  </div>
</template>
